package com.dotdrives.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

@Serializable
data class Target(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val name: String,
    @SerialName("goal_amount")
    val goalAmount: Double?,
    @SerialName("is_active")
    val isActive: Boolean,
    @SerialName("goal_starts_on") val goalStartsOn: String? = null,
    @SerialName("goal_ends_on") val goalEndsOn: String? = null,
)

@Serializable
data class DrivePhaseTemplate(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    val name: String,
    @SerialName("is_default")
    val isDefault: Boolean,
    @SerialName("is_active")
    val isActive: Boolean,
    @SerialName("is_visible")
    val isVisible: Boolean,
)

@Serializable
data class DrivePhase(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("template_id")
    val templateId: String,
    val name: String,
    val description: String,
    @SerialName("example_text")
    val exampleText: String,
    val sequence: Int,
    @SerialName("is_terminal") val isTerminal: Boolean = false,
)

@Serializable
data class DrivePhaseTask(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("phase_id")
    val phaseId: String,
    val name: String,
    val category: String,
    val sequence: Int,
)

data class DriveTaskItem(
    val id: String,
    val driveId: String,
    val phaseTaskId: String,
    val name: String,
    val category: String,
    val sequence: Int,
    val isComplete: Boolean,
    val completedBy: String?,
    val completedAt: Instant?,
)

/** One board-card / list-row worth of drive data. */
@Serializable
data class DriveSummary(
    val id: String,
    val title: String,
    val giverId: String,
    val giverDisplayName: String,
    val giverType: String,
    val currentPhaseId: String,
    val targetId: String?,
    val targetName: String?,
    val targetText: String?,
    val tags: List<String>,
    val askAmount: Double?,
    val stewardUserId: String?,
    val stewardName: String?,
    val phaseEnteredAt: Instant,
    val tasksComplete: Int,
    val tasksTotal: Int,
    val outcome: String?,
    val updatedAt: Instant,
    val archivedAt: Instant? = null,
    val closedAt: Instant? = null,
) {
    val target: String?
        get() = targetName ?: targetText
}

data class Drive(
    val id: String,
    val orgId: String,
    val title: String,
    val giverId: String,
    val giverDisplayName: String,
    val advocateGiverId: String?,
    val advocateGiverName: String?,
    val stewardUserId: String,
    val stewardName: String?,
    val templateId: String,
    val currentPhaseId: String,
    val currentPhaseName: String?,
    val phaseEnteredAt: Instant,
    val targetId: String?,
    val targetName: String?,
    val targetText: String?,
    val askAmount: Double?,
    val outcome: String?,
    val tags: List<String>,
    val archivedAt: Instant?,
    val createdAt: Instant,
    val updatedAt: Instant,
) {
    val target: String?
        get() = targetName ?: targetText
}

/** One row per drive, gated on `donation.read` — null/absent for a field-role user. */
@Serializable
data class DriveFinancials(
    val driveId: String,
    val proposedAmount: Double,
    val pledgedAmount: Double,
    val receivedAmount: Double,
)

data class NewDrive(
    val title: String,
    val giverId: String,
    val advocateGiverId: String?,
    val stewardUserId: String,
    val templateId: String,
    val targetId: String?,
    val targetText: String?,
    val askAmount: Double?,
    val initialPhaseId: String? = null,
)

data class DriveFile(
    val id: String,
    val fileName: String,
    val fileSize: Long,
    val storagePath: String,
    val uploadedBy: String,
    val createdAt: Instant,
)
